import { sortBy } from "lodash-es"
import * as SMART_ON_FHIR from "utils/smartOnFhirHelpers"
import {
  PATIENT_FIRST_NAME_QUESTION_TAG,
  PATIENT_LAST_NAME_QUESTION_TAG,
  PATIENT_FULL_NAME_QUESTION_TAG,
  PATIENT_MEDICAL_RECORD_NUMBER_QUESTION_TAG,
  PATIENT_SEX_QUESTION_TAG,
  PATIENT_DATE_OF_BIRTH_QUESTION_TAG,
  PATIENT_ADDRESS_STREET_ONE_QUESTION_TAG,
  PATIENT_ADDRESS_STREET_TWO_QUESTION_TAG,
  PATIENT_ADDRESS_CITY_QUESTION_TAG,
  PATIENT_ADDRESS_STATE_QUESTION_TAG,
  PATIENT_ADDRESS_ZIP_CODE_QUESTION_TAG,
  PATIENT_CELL_PHONE_QUESTION_TAG,
  PATIENT_HOME_PHONE_QUESTION_TAG,
  PATIENT_GENDER_IDENTITY_QUESTION_TAG,
  PATIENT_RACE_QUESTION_TAG,
} from "./patientQuestionTags"

// Removes a leading + for country code from phone number:
// "+1 608-555-9000 + 2345" to "1 608-555-9000 + 2345"
const sanitizePhoneNumber = (phoneNumber) => phoneNumber.replace(/^\+/, "")

class PatientDataReader {
  constructor(fhirPatientData, ehrName) {
    this.fhirPatientData = fhirPatientData
    this.ehrName = ehrName
  }

  // Look for the name with a "use" property with the value "official".
  // If none is found, then fallback to the first name in the list.
  // Meditech patient name data does not appear to have a "use" property,
  // so we would fall back to the name listed first.
  patientOfficialNameNode() {
    return this.fhirPatientData?.name?.find((node) => (
      node.use?.match(/official/i)
    )) || this.fhirPatientData?.name?.[0]
  }

  // Athena data contains only one identifier, so we use that as the MRN node.
  //
  // For Epic, we find the identifier by OID (see https://vendorservices.epic.com/Article/Index?docid=epicidtypes).
  // An MRN OID ends with a value of "5.737384.14" (e.g., 1.2.840.114350.1.13.0.1.7.5.737384.14);
  // see the linked documentation for a description of the rest of the OID.
  //
  // Cerner and Meditech patient identifiers contain a "type" property
  // with a "coding" list.  We look for a matching coding display value.
  // This is the default way to find the node.
  patientMedicalRecordNode() {
    if (this.ehrName === SMART_ON_FHIR.ATHENA) {
      return this.fhirPatientData?.identifier?.[0]
    }

    if (this.ehrName === SMART_ON_FHIR.EPIC) {
      return this.fhirPatientData?.identifier?.find((node) => (
        node.system?.endsWith("5.737384.14")
      ))
    }

    return this.fhirPatientData?.identifier?.find((node) => (
      node.type?.coding?.[0]?.display?.match(/(mrn)|(medical\srecord\snumber)/i)
    ))
  }

  patientAddressNode() {
    const homeAddress = this.fhirPatientData?.address?.find((node) => (
      node.use?.match(/home/i)
    ))

    return homeAddress || this.fhirPatientData?.address?.[0]
  }

  patientCellPhoneNode() {
    const mobileNumbers = this.fhirPatientData?.telecom?.filter((node) => (
      node.system?.match(/phone/i) && node.use?.match(/mobile/i)
    ))

    return mobileNumbers ? sortBy(mobileNumbers, ["rank"])[0] : undefined
  }

  patientHomePhoneNode() {
    const homeNumbers = this.fhirPatientData?.telecom?.filter((node) => (
      node.system?.match(/phone/i) && node.use?.match(/home/i)
    ))

    return homeNumbers ? sortBy(homeNumbers, ["rank"])[0] : undefined
  }

  // A client using Athena requested a gender identity value, which is listed in the FHIR data
  // as an extension.  It's unknown whether this extension is generally available
  // from other EHRs, so we'll only look for it from Athena.
  patientGenderIdentityNode() {
    if (this.ehrName === SMART_ON_FHIR.ATHENA) {
      return this.fhirPatientData?.extension?.find((node) => (
        node.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity"
      ))
    }

    return undefined
  }

  // An Epic client requested a patient race value, assuming based on HL7 documentation
  // that other EHR's will operate similarly.
  //
  // http://hl7.org/fhir/us/core/StructureDefinition/us-core-race
  // https://hl7.org/fhir/us/core/STU8.0.1/Patient-example.json.html
  patientRaceNode() {
    const raceExtensionData = this.fhirPatientData?.extension?.find((node) => (
      node.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race"
    ))

    return raceExtensionData?.extension?.find((node) => node.url?.toLowerCase() === "text")
  }

  get [PATIENT_FIRST_NAME_QUESTION_TAG]() {
    return this.patientOfficialNameNode()?.given?.join(" ") ?? ""
  }

  get [PATIENT_LAST_NAME_QUESTION_TAG]() {
    return this.patientOfficialNameNode()?.family ?? ""
  }

  // Athena names do not contain a "text" attribute,
  // so we combine the first and last names into the full name.
  get [PATIENT_FULL_NAME_QUESTION_TAG]() {
    if (this.ehrName === SMART_ON_FHIR.ATHENA) {
      return `${this[PATIENT_FIRST_NAME_QUESTION_TAG]} ${this[PATIENT_LAST_NAME_QUESTION_TAG]}`.trim()
    }

    return this.patientOfficialNameNode()?.text ?? ""
  }

  // Credible patient data does not contain an MRN in the identifier list,
  // so the patient resource's own id is used as the MRN.
  //
  // For Athena, the patient's medical record number (MRN) needs to be parsed
  // from the node's value.  The value is formatted like "a-195900.E-60184"
  // where the actual MRN is whatever trails the last dash.
  get [PATIENT_MEDICAL_RECORD_NUMBER_QUESTION_TAG]() {
    if (this.ehrName === SMART_ON_FHIR.CREDIBLE) {
      return this.fhirPatientData?.id ?? ""
    }

    const mrn = this.patientMedicalRecordNode()?.value

    if (!mrn) return ""

    if (this.ehrName === SMART_ON_FHIR.ATHENA) {
      return mrn.split("-")[2]
    }

    return mrn
  }

  get [PATIENT_SEX_QUESTION_TAG]() {
    return this.fhirPatientData?.gender ?? ""
  }

  get [PATIENT_DATE_OF_BIRTH_QUESTION_TAG]() {
    return this.fhirPatientData?.birthDate ?? ""
  }

  get [PATIENT_ADDRESS_STREET_ONE_QUESTION_TAG]() {
    return this.patientAddressNode()?.line?.[0] ?? ""
  }

  get [PATIENT_ADDRESS_STREET_TWO_QUESTION_TAG]() {
    return this.patientAddressNode()?.line?.[1] ?? ""
  }

  get [PATIENT_ADDRESS_CITY_QUESTION_TAG]() {
    return this.patientAddressNode()?.city ?? ""
  }

  get [PATIENT_ADDRESS_STATE_QUESTION_TAG]() {
    return this.patientAddressNode()?.state ?? ""
  }

  get [PATIENT_ADDRESS_ZIP_CODE_QUESTION_TAG]() {
    return this.patientAddressNode()?.postalCode ?? ""
  }

  get [PATIENT_CELL_PHONE_QUESTION_TAG]() {
    const cellPhoneNumber = this.patientCellPhoneNode()?.value

    if (!cellPhoneNumber) return ""

    return sanitizePhoneNumber(cellPhoneNumber)
  }

  get [PATIENT_HOME_PHONE_QUESTION_TAG]() {
    const homePhoneNumber = this.patientHomePhoneNode()?.value

    if (!homePhoneNumber) return ""

    return sanitizePhoneNumber(homePhoneNumber)
  }

  // Only applies to Athena.  For the other EHRs, the node will return as undefined.
  get [PATIENT_GENDER_IDENTITY_QUESTION_TAG]() {
    return this.patientGenderIdentityNode()?.valueCodeableConcept?.text ?? ""
  }

  get [PATIENT_RACE_QUESTION_TAG]() {
    return this.patientRaceNode()?.valueString ?? ""
  }
}

export default PatientDataReader
